#!/usr/bin/env node

import { program } from 'commander';
import chalk from 'chalk';
import { ProxyServer } from './core/proxy-server.js';
import { AuthMiddleware } from './core/auth.js';
import type { ProxyConfig } from './types.js';
import * as dotenv from 'dotenv';

// 加载环境变量
dotenv.config();

program
  .name('proxy-server')
  .description('HTTP/HTTPS proxy server with authentication and rate limiting')
  .version('0.1.0');

program
  .command('start')
  .description('Start the proxy server')
  .option('-p, --port <number>', 'Server port', '8080')
  .option('-h, --host <string>', 'Server host', '0.0.0.0')
  .option('--auth', 'Enable authentication')
  .option('--token <string>', 'Authentication token (or use PROXY_AUTH_TOKEN env var)')
  .option('--rate-limit <number>', 'Max requests per minute', '100')
  .option('--log-level <level>', 'Log level (debug|info|warn|error)', 'info')
  .action(async (options) => {
    const config: ProxyConfig = {
      port: parseInt(options.port),
      host: options.host,
      auth: {
        enabled: options.auth,
        token: options.token,
      },
      rateLimit: {
        maxRequests: parseInt(options.rateLimit),
      },
      logging: {
        level: options.logLevel as any,
        pretty: true,
      },
    };

    console.log(chalk.blue('🚀 Starting proxy server...'));
    
    try {
      const server = new ProxyServer(config);
      await server.start();
      
      console.log(chalk.green(`✅ Proxy server running on ${config.host}:${config.port}`));
      
      if (config.auth?.enabled) {
        console.log(chalk.yellow('🔒 Authentication is enabled'));
      }
      
      console.log(chalk.gray('\nPress Ctrl+C to stop the server'));

      // 优雅关闭
      process.on('SIGINT', async () => {
        console.log(chalk.yellow('\n\n⏹️  Shutting down...'));
        await server.stop();
        process.exit(0);
      });

      process.on('SIGTERM', async () => {
        await server.stop();
        process.exit(0);
      });
    } catch (error) {
      console.error(chalk.red('❌ Failed to start server:'), error);
      process.exit(1);
    }
  });

program
  .command('generate-token')
  .description('Generate a new authentication token')
  .action(() => {
    const token = AuthMiddleware.generateToken();
    console.log(chalk.green('🔑 Generated token:'), token);
    console.log(chalk.gray('\nSet this token in your environment:'));
    console.log(chalk.blue(`export PROXY_AUTH_TOKEN="${token}"`));
  });

program
  .command('test')
  .description('Test proxy connection')
  .option('-u, --url <string>', 'Proxy server URL', 'http://localhost:8080')
  .option('-t, --target <string>', 'Target URL to test', 'https://api.anthropic.com/health')
  .option('--token <string>', 'Authentication token')
  .action(async (options) => {
    const { default: fetch } = await import('node-fetch');
    
    console.log(chalk.blue('🧪 Testing proxy connection...'));
    console.log(chalk.gray(`Proxy: ${options.url}`));
    console.log(chalk.gray(`Target: ${options.target}`));

    try {
      const headers: any = {
        'X-Target-URL': options.target,
      };

      if (options.token) {
        headers['Authorization'] = `Bearer ${options.token}`;
      }

      const response = await fetch(`${options.url}/`, {
        headers,
      });

      if (response.ok) {
        console.log(chalk.green('✅ Proxy is working!'));
        console.log(chalk.gray(`Status: ${response.status} ${response.statusText}`));
      } else {
        console.log(chalk.red('❌ Proxy returned error'));
        console.log(chalk.gray(`Status: ${response.status} ${response.statusText}`));
        const body = await response.text();
        console.log(chalk.gray(`Response: ${body}`));
      }
    } catch (error) {
      console.error(chalk.red('❌ Connection failed:'), error);
    }
  });

// 示例命令
program
  .command('example')
  .description('Show usage examples')
  .action(() => {
    console.log(chalk.bold('📚 Usage Examples:\n'));
    
    console.log(chalk.green('1. Start proxy server:'));
    console.log('   proxy-server start');
    console.log('   proxy-server start --port 3128 --auth');
    
    console.log(chalk.green('\n2. Generate authentication token:'));
    console.log('   proxy-server generate-token');
    
    console.log(chalk.green('\n3. Test proxy connection:'));
    console.log('   proxy-server test --token YOUR_TOKEN');
    
    console.log(chalk.green('\n4. Use with codem:'));
    console.log('   codem create myenv --proxy http://localhost:8080');
    
    console.log(chalk.green('\n5. Use with curl:'));
    console.log('   curl -x http://localhost:8080 https://api.anthropic.com');
    console.log('   curl -H "X-Target-URL: https://api.anthropic.com" http://localhost:8080/');
  });

program.parse();